<template>
  <div ref="rootEl" class="relative">
    <InputText
      v-model="model"
      autocomplete="off"
      :placeholder="placeholder"
      :disabled="disabled"
      v-bind="inputAttrs"
      @update:model-value="onAddressInput"
      @keydown="handleKeydown"
      @focusin="isFocused = true"
      @focusout="handleFocusout"
    />

    <ul
      v-if="open"
      data-testid="location-dropdown"
      class="surface absolute top-full right-0 left-0 z-40 mt-1 max-h-64 overflow-auto rounded-md py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
    >
      <li
        v-if="trimmedQuery.length < LOCATION_AUTOCOMPLETE_MIN_CHARS"
        class="px-2 py-1 text-gray-500"
      >
        Typ minimaal {{ LOCATION_AUTOCOMPLETE_MIN_CHARS }} tekens om een locatie te zoeken…
      </li>
      <li v-else-if="loading && items.length === 0" class="px-2 py-1 text-gray-500">Zoeken…</li>
      <li v-else-if="items.length === 0" class="px-2 py-1 text-gray-500">Geen locaties gevonden</li>
      <li
        v-for="(item, index) in items"
        :key="item.key"
        data-testid="location-suggestion"
        :data-source="item.source"
        :data-highlighted="index === highlightedIndex ? 'true' : 'false'"
        class="hover:bg-secondary/10 flex cursor-pointer items-center gap-x-2 px-2 py-1 select-none"
        :class="index === highlightedIndex ? 'bg-secondary/10' : ''"
        @mouseenter="highlightedIndex = index"
        @mousedown.prevent="pickSuggestion(item)"
      >
        <span
          class="inline-block w-12 shrink-0 text-[10px] tracking-wide uppercase"
          :class="item.source === 'saved' ? 'text-secondary' : 'text-gray-400'"
        >
          {{ item.source === "saved" ? "Opgesl." : "Maps" }}
        </span>
        <span class="truncate">{{ item.label }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, useAttrs } from "vue"
import { storeToRefs } from "pinia"
import { onClickOutside } from "@vueuse/core"
import InputText from "@/components/inputs/InputText.vue"
import useDelayedFunction from "@/composables/input/delayed-function.ts"
import type { LocationController } from "@/controllers/model-controllers/location-controller"
import { useAuthStore } from "@/store/auth.store"
import {
  LOCATION_AUTOCOMPLETE_MIN_CHARS,
  mergeFirstSuggestionStreetWithHouseNumber,
  searchLocationSuggestions,
  shouldApplyFirstSuggestionStreet,
  useSuggestionList,
  type LocationSuggestion
} from "@/composables/location/use-location-autocomplete"

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    placeholder?: string
    disabled?: boolean
    /** A location id to keep out of the saved suggestions (e.g. the one being edited). */
    excludeId?: string
  }>(),
  {
    placeholder: "Zoek een adres…",
    disabled: false
  }
)

const emit = defineEmits<{ select: [item: LocationSuggestion] }>()
const model = defineModel<string>({ default: "" })
const isFocused = ref(false)
let skipNextSearch = false

const attrs = useAttrs()
const inputAttrs = computed(() => {
  const forwarded = { ...(attrs as Record<string, unknown>) }
  delete forwarded.placeholder
  delete forwarded.disabled
  return forwarded
})

const locationController = inject<LocationController>("locationController")
const { activeUser } = storeToRefs(useAuthStore())
const rootEl = ref<HTMLElement>()
const { open, loading, items, highlightedIndex, query, trimmedQuery } = useSuggestionList()
let searchGeneration = 0

onClickOutside(rootEl, () => {
  if (open.value) closeMenu()
})

async function runSearch(q: string): Promise<LocationSuggestion[]> {
  const generation = ++searchGeneration
  loading.value = true
  try {
    const results = await searchLocationSuggestions(q, {
      locationController,
      apiKey: activeUser.value?.activeTenant?.tenantSettings?.apiKeyGoogleMaps ?? "",
      excludeId: props.excludeId
    })
    if (generation !== searchGeneration || !open.value) return []
    items.value = results
    highlightedIndex.value = 0
    return results
  } finally {
    if (generation === searchGeneration) loading.value = false
  }
}

const delayedSearch = useDelayedFunction(async (q: string) => {
  await runSearch(q)
}, 250)

function closeMenu() {
  open.value = false
  items.value = []
  highlightedIndex.value = 0
  delayedSearch.cancel()
  searchGeneration++
  loading.value = false
}

function onAddressInput(value: string | null | undefined) {
  if (!isFocused.value || skipNextSearch) {
    skipNextSearch = false
    return
  }
  const next = value ?? ""
  const trimmed = next.trim()
  const lastChar = next.slice(-1)

  if (
    open.value &&
    items.value.length > 0 &&
    /^\d$/.test(lastChar) &&
    shouldApplyFirstSuggestionStreet(trimmed, trimmed.slice(0, -1))
  ) {
    const highlighted = items.value[highlightedIndex.value] ?? items.value[0]
    if (!highlighted) return
    const merged = mergeFirstSuggestionStreetWithHouseNumber(trimmed, highlighted)
    if (merged && merged !== trimmed) {
      query.value = merged
      model.value = merged
      open.value = true
      delayedSearch.schedule(merged)
      return
    }
  }

  query.value = next
  if (!next.trim()) {
    closeMenu()
    return
  }
  open.value = true
  if (next.trim().length < LOCATION_AUTOCOMPLETE_MIN_CHARS) {
    items.value = []
    loading.value = false
    return
  }
  delayedSearch.schedule(next.trim())
}

async function pickSuggestion(item: LocationSuggestion) {
  closeMenu()
  skipNextSearch = true
  emit("select", item)
}

async function pickHighlightedOrFirstSuggestion() {
  let item = items.value[highlightedIndex.value] ?? items.value[0]
  if (!item && trimmedQuery.value.length >= LOCATION_AUTOCOMPLETE_MIN_CHARS) {
    delayedSearch.cancel()
    const results = await runSearch(trimmedQuery.value)
    item = results[0]
  }
  if (item) await pickSuggestion(item)
}

function handleKeydown(event: KeyboardEvent) {
  if (!open.value) return

  switch (event.key) {
    case "ArrowDown":
    case "ArrowUp": {
      event.preventDefault()
      const delta = event.key === "ArrowDown" ? 1 : -1
      const count = items.value.length
      if (count) highlightedIndex.value = (highlightedIndex.value + delta + count) % count
      break
    }
    case "Escape":
      event.preventDefault()
      closeMenu()
      break
    case "Enter":
    case "Tab":
      event.preventDefault()
      void pickHighlightedOrFirstSuggestion()
      break
  }
}

function handleFocusout() {
  window.setTimeout(() => {
    if (!rootEl.value?.contains(document.activeElement)) closeMenu()
  })
}
</script>
